'use client';

import { useState, useEffect } from 'react';
import { X, ArrowRight } from 'lucide-react';

interface TutorialStep {
  target: string; // description of what we're highlighting
  title: string;
  description: string;
  position: 'top' | 'middle' | 'bottom';
}

const steps: TutorialStep[] = [
  {
    target: 'campaign',
    title: 'Campaign',
    description: 'Your main workflow. Set up outreach, manage leads, and generate personalized emails.',
    position: 'top',
  },
  {
    target: 'settings',
    title: 'Settings',
    description: 'Configure your company profile, sender info, and preferences. AI uses this to write emails that sound like you.',
    position: 'middle',
  },
  {
    target: 'theme',
    title: 'Theme & Extension',
    description: 'Switch between light and dark mode. The status dot shows if the Chrome extension is connected for Gmail drafts.',
    position: 'bottom',
  },
];

export function SidebarTutorial() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const seen = localStorage.getItem('tutorial_seen');
    if (!seen) setVisible(true);
  }, []);

  function dismiss() {
    setVisible(false);
    localStorage.setItem('tutorial_seen', 'true');
  }

  function next() {
    if (step < steps.length - 1) setStep(step + 1);
    else dismiss();
  }

  if (!visible) return null;

  const current = steps[step];
  const topOffset =
    current.position === 'top' ? 'top-20' :
    current.position === 'middle' ? 'top-36' :
    'bottom-28';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 animate-fade-in"
        onClick={dismiss}
      />

      {/* Tooltip */}
      <div
        className={`fixed left-[232px] ${topOffset} z-50 animate-fade-in`}
      >
        <div className="bg-base-200 border border-base-300 rounded-xl p-4 w-72 shadow-xl">
          {/* Arrow pointing left */}
          <div className="absolute left-0 top-4 -translate-x-full">
            <div className="border-8 border-transparent border-r-base-200" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">{current.title}</h3>
            <button
              onClick={dismiss}
              className="btn btn-ghost btn-xs btn-circle text-base-content/30"
            >
              <X size={12} />
            </button>
          </div>

          {/* Body */}
          <p className="text-xs text-base-content/60 leading-relaxed mb-4">
            {current.description}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    i === step ? 'bg-primary' : 'bg-base-content/15'
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={dismiss} className="text-xs text-base-content/30 hover:text-base-content/50">
                Skip
              </button>
              <button
                onClick={next}
                className="btn btn-primary btn-xs gap-1"
              >
                {step < steps.length - 1 ? (
                  <>Next <ArrowRight size={10} /></>
                ) : (
                  'Got it'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
